import React from 'react'

interface PythonGuideModalProps {
  isOpen: boolean
  onClose: () => void
  moduleTitle?: string
}

export const PythonGuideModal: React.FC<PythonGuideModalProps> = ({ isOpen, onClose, moduleTitle }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 w-full max-w-2xl rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Python Usage Guide{moduleTitle ? ` — ${moduleTitle}` : ''}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">✕</button>
        </div>
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          <div className="prose dark:prose-invert max-w-none">
            <h3>Quick Start (In‑Browser)</h3>
            <ul>
              <li>Runs with Pyodide in your browser. No installation needed.</li>
              <li>Best for small scripts and learning examples.</li>
              <li>Packages preloaded: numpy, pandas, matplotlib. Additional packages may be unavailable.</li>
            </ul>

            <h3>Local Setup (Recommended for Larger Examples)</h3>
            <p>Requirements: Python 3.10+ (3.11 preferred), pip, 2 GB RAM minimum (4+ GB recommended).</p>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-md overflow-x-auto text-sm"><code>{`# 1) Create and activate a virtual environment
python -m venv .venv
# macOS/Linux
source .venv/bin/activate
# Windows (PowerShell)
.venv\\Scripts\\Activate.ps1

# 2) Upgrade pip and install common packages
python -m pip install --upgrade pip
pip install numpy pandas matplotlib scipy scikit-learn jupyter

# 3) (Optional) For plotting in notebooks
pip install jupyterlab

# 4) Run your script
python script.py

# 5) Or start a notebook
jupyter lab  # or: jupyter notebook`}</code></pre>

            <h3>Running the Code</h3>
            <ul>
              <li>Copy the code via the Copy button, or Download it as a .py file.</li>
              <li>If the example expects data files, adjust the file paths locally.</li>
              <li>For time‑series or plots, prefer local Python for performance and richer libraries.</li>
            </ul>

            <h3>Troubleshooting</h3>
            <ul>
              <li>ImportError locally: run <code>pip install package-name</code>.</li>
              <li>Pyodide errors: reload the page, or use the local setup.</li>
              <li>Memory errors: close other tabs/apps or move to local Python.</li>
            </ul>

            <h3>Security and Data</h3>
            <ul>
              <li>Do not paste secrets or proprietary data into the browser editor.</li>
              <li>Persist data locally using your venv and filesystem.</li>
            </ul>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100">Close</button>
        </div>
      </div>
    </div>
  )
}

export default PythonGuideModal